/**
 * OVERVOLT meta-progression — the persistent player **profile**.
 *
 * The profile is **between-run meta state** (docs/GAME_DESIGN.md §19): the
 * **Flux** currency, the purchased **unlock** ids, the **Scoreboard** (§20)
 * personal bests + run history, and lifetime stats. It lives only in
 * `localStorage` — it is **never** part of `GameState`, and the simulation
 * (`game/systems.ts`) never reads it, so a run with identical `(seed, mode,
 * difficulty, inputs)` is byte-identical for any profile (the §4 determinism
 * contract). The unlock *gate* is a UI-only filter; this module is the data
 * model + persistence + the pure award/purchase functions.
 *
 * Every value written here is a deterministic function of the settled run, so
 * a test can drive N runs to known outcomes and assert the exact profile.
 */
import {
  CONFIG,
  type AbilityId,
  type Difficulty,
  type RunMode,
  type TowerType,
  type UnlockGrant,
} from '../config';

/**
 * Profile blob schema version. {@link coerceProfile} is a forward-migration
 * (n1) — older blobs are coerced field-by-field and re-stamped to the current
 * version, so a bump never wipes existing players.
 *
 * - **v1** — Sprint 9 baseline: flux, unlocks, bests, runHistory, stats.
 * - **v2** — Sprint 25 (tutorial-s25): adds `seenRoleHint`, a per-tower-type
 *   first-placement hint-tour flag map (parallels `overvolt.seenIntro` in
 *   `localStorage`, but lives in the profile so it survives a key clear and
 *   reads naturally as "what has this player been shown").
 * - **v3** — Sprint 53 (tutorial-flow-s53): adds `seenContextHint`, a
 *   per-context-id one-shot map mirroring `seenRoleHint`'s shape. Each entry
 *   gates one additional onboarding hint (the Overload dial, the P-key
 *   Profile hint at wave 3, the Endless modifier intro, the puzzle-mode
 *   chip hover) — see `Onboarding.fireContextHint`. A v2 profile coerces
 *   forward with an empty `seenContextHint` map, so the hints fire naturally
 *   for returning players who never saw them.
 * - **v4** — Sprint 62 (auth-frontend-impl-s62): adds two fields wired to
 *   the new optional sign-in flow (see `docs/AUTH_DESIGN.md` §3 / §5):
 *   `guestMode: boolean | null` — `null` while the player has not yet picked
 *   on the first-visit start-screen modal (so the modal shows), `true` once
 *   they pick "Play as guest", `false` once they sign in (not reachable in
 *   this sprint — backend is stubbed). `cookiesChoice: 'accepted' |
 *   'essentials' | null` — `null` while the cookies banner has not yet been
 *   resolved (so the banner shows), and the player's choice otherwise. A
 *   v3 profile coerces forward with both fields `null`, so a returning
 *   player sees the start-screen modal + the banner exactly once.
 * - **v5** — Sprint 73 (tutorial-impl-s73): adds `seenFullTutorial: boolean`
 *   — a single one-shot flag gating the title-screen "Start Tutorial"
 *   button's first-time pulse vs. "Replay tour" affordance (see
 *   `docs/TUTORIAL_DESIGN.md` §5). `false` by default on fresh profiles; the
 *   completion (or skip) of the 12-step opt-in interactive tour flips it to
 *   `true`. Coerces forward field-by-field from every prior schema (v1
 *   through v4): a returning v4 player carries through their `guestMode` /
 *   `cookiesChoice`; a v3 player picks up the v4 nulls and the v5 default
 *   simultaneously; a v2 player gets `seenContextHint = {}` AND
 *   `seenFullTutorial = false`; a v1 player gets every later field as its
 *   fresh default. None of the migrations re-fires any one-shot the player
 *   has already seen — they only fire the surfaces they never saw.
 */
export const PROFILE_VERSION = 5;

/** A personal-best entry for one `(mode, difficulty)` cell of the Scoreboard. */
export interface BestEntry {
  /** Best final score (§9). */
  score: number;
  /** Best wave — `wavesCleared` for Campaign/Maze, deepest reached for Endless. */
  wave: number;
}

/** Personal bests, keyed `bests[mode][difficulty]` — see docs/GAME_DESIGN.md §20.1. */
export type BestsMap = Record<RunMode, Partial<Record<Difficulty, BestEntry>>>;

/** One completed run, recorded in the Scoreboard run history (§20.1). */
export interface RunRecord {
  /** Monotonic integer run index (= `stats.runsPlayed` when recorded). */
  run: number;
  mode: RunMode;
  difficulty: Difficulty;
  /** Count of fully-cleared waves (a Campaign win = `campaignLength`). */
  wavesCleared: number;
  /** Final score (§9). */
  score: number;
  /** True for a Campaign/Maze win. */
  won: boolean;
  /** Highest Overload tier locked in during the run. */
  overloadMax: number;
  /** Flux this run paid out (§19.1). */
  fluxEarned: number;
}

/** Lifetime aggregate stats — see docs/GAME_DESIGN.md §20.1. */
export interface ProfileStats {
  runsPlayed: number;
  campaignWins: number;
  totalFluxEarned: number;
  bestScoreOverall: number;
  /**
   * Deepest Endless wave ever reached — the *true* deepest, tracked apart from
   * `bests.endless` (whose `wave` is the best-*scoring* run's wave, not the
   * deepest). A deep low-scoring run still raises this (m2).
   */
  deepestEndlessWave: number;
}

/**
 * One-time first-placement hint-tour seen flags — `seenRoleHint[type] === true`
 * once the player has placed their first tower of `type` on this profile and
 * the role-hint toast has been shown for it. A Set-like map; absent / `false`
 * means "not yet shown" (Sprint 25, tutorial-s25). Mirrors the existing
 * one-shot `overvolt.seenIntro` pattern but is per-tower-type, so a player
 * who unlocked Mortar mid-progression still gets its hint the first time they
 * place one.
 */
export type SeenRoleHintMap = Partial<Record<TowerType, boolean>>;

/**
 * Sprint 53 (tutorial-flow-s53) — context-hint ids, each gating one extra
 * onboarding toast keyed on a specific player action / state edge:
 *
 * - **`overload`** — the first time the player cranks the Overload dial past
 *   tier 0; the toast explains the risk / reward trade.
 * - **`profileKey`** — the first wave reaching `state.wave >= 3` (the player
 *   has at minimum played past the early game); the toast points to the
 *   `P` key for the Profile / unlock tree.
 * - **`endlessMod`** — the first time the player enters Endless mode (whether
 *   from a Continue → Endless transition or a pre-run Endless select); the
 *   toast names the per-wave MODIFIER rotation (Sprint 47).
 * - **`puzzleHover`** — the first time the cursor enters the title-screen
 *   puzzle-chip hover region; the toast explains Puzzle is a separate
 *   game (LOS walls, hand-crafted logic levels).
 *
 * Mirrors {@link SeenRoleHintMap} — one-shot per profile, absent / `false`
 * means "not yet shown". Persisted in the profile so the hints survive a
 * `localStorage` key clear of the `overvolt.seenIntro` flag.
 */
export type ContextHintId = 'overload' | 'profileKey' | 'endlessMod' | 'puzzleHover';

/** One-shot context-hint seen flags — Sprint 53 sibling to {@link SeenRoleHintMap}. */
export type SeenContextHintMap = Partial<Record<ContextHintId, boolean>>;

/** Every {@link ContextHintId} value — the keys of `Profile.seenContextHint`. */
export const CONTEXT_HINT_IDS: readonly ContextHintId[] = [
  'overload',
  'profileKey',
  'endlessMod',
  'puzzleHover',
];

/**
 * Sprint 62 (auth-frontend-impl-s62) — the player's cookies-banner choice
 * (see `docs/AUTH_DESIGN.md` §5). `null` while the banner has not yet been
 * resolved (so the banner shows on first visit); `'accepted'` enables the
 * future session-cookie persistence path; `'essentials'` is a per-tab
 * sign-in only — the next reload signs out. Stored in the profile blob, not
 * a separate `localStorage` key, so a profile reset / clear naturally
 * re-shows the banner.
 */
export type CookiesChoice = 'accepted' | 'essentials';

/** The full persistent profile blob (`localStorage` key `config.profileKey`). */
export interface Profile {
  /** Schema version — {@link PROFILE_VERSION}. */
  version: number;
  /** Persistent Flux balance (§19.1). */
  flux: number;
  /** Purchased unlock-node ids (§19.2). */
  unlocked: string[];
  /** Personal-best grid (§20). */
  bests: BestsMap;
  /** The last `config.runHistoryMax` completed runs, newest last (§20.1). */
  runHistory: RunRecord[];
  /** Lifetime aggregates (§20.1). */
  stats: ProfileStats;
  /** Per-tower-type "first-placement hint already shown" flags (Sprint 25). */
  seenRoleHint: SeenRoleHintMap;
  /** Per-context-id "context hint already shown" flags (Sprint 53). */
  seenContextHint: SeenContextHintMap;
  /**
   * Sprint 62 — the start-screen modal choice. `null` on first visit (so the
   * modal shows); `true` once the player picks "Play as guest"; `false` once
   * they sign in (the signed-in path is stubbed in this sprint — backend
   * lives in a separate sprint; see `docs/AUTH_DESIGN.md` §3 / §7.2). The
   * modal re-shows only after an explicit Sign-out (which resets this back
   * to `null`).
   */
  guestMode: boolean | null;
  /**
   * Sprint 62 — the cookies-banner choice. `null` on first visit (so the
   * banner shows); the player's pick once resolved. See {@link CookiesChoice}.
   */
  cookiesChoice: CookiesChoice | null;
  /**
   * Sprint 73 (tutorial-impl-s73) — one-shot "has this player seen the full
   * 12-step opt-in interactive tour at least once" flag. `false` on a fresh
   * profile (so the title-screen Start Tutorial button pulses + reads "First
   * time? Take the tour"); `true` once the player completes OR skips the
   * tour (both paths count — a skip is consent to not see the prompt
   * again). The flag does **not** suppress launching the tour — clicking
   * the button always restarts at step 1; the flag only controls the
   * button's resting label and pulse. See `docs/TUTORIAL_DESIGN.md` §5.
   */
  seenFullTutorial: boolean;
}

/** The settled outcome of one run — the pure input to {@link settleRun}. */
export interface RunOutcome {
  mode: RunMode;
  difficulty: Difficulty;
  /** Count of fully-cleared waves. */
  wavesCleared: number;
  /** Deepest wave reached (= the wave number lost on, or `wavesCleared` on a win). */
  deepestWave: number;
  /** Final score (§9). */
  finalScore: number;
  /** True for a Campaign/Maze win. */
  won: boolean;
  /** Highest Overload tier locked in during the run. */
  overloadMax: number;
}

/** The three run modes — the row order of the Scoreboard grid. */
export const RUN_MODES: readonly RunMode[] = ['campaign', 'endless', 'maze'];
/** The three difficulties — the column order of the Scoreboard grid. */
export const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'hard'];
/** Every {@link TowerType} value — the keys of `Profile.seenRoleHint` (Sprint 25). */
const TOWER_TYPES: readonly TowerType[] = [
  'pulse',
  'arc',
  'pylon',
  'cryo',
  'mortar',
  'sniper',
  'wall',
];

/** A fresh, empty bests grid. */
function emptyBests(): BestsMap {
  return { campaign: {}, endless: {}, maze: {} };
}

/** Build a fresh-player profile — 0 Flux, nothing unlocked, empty history. */
export function createDefaultProfile(): Profile {
  return {
    version: PROFILE_VERSION,
    flux: 0,
    unlocked: [],
    bests: emptyBests(),
    runHistory: [],
    stats: {
      runsPlayed: 0,
      campaignWins: 0,
      totalFluxEarned: 0,
      bestScoreOverall: 0,
      deepestEndlessWave: 0,
    },
    seenRoleHint: {},
    seenContextHint: {},
    // Sprint 62 — both fields start `null` so the start-screen modal and the
    // cookies banner fire on a fresh profile's first visit. The player's pick
    // re-stamps them; a sign-out resets `guestMode` back to `null`.
    guestMode: null,
    cookiesChoice: null,
    // Sprint 73 — fresh profile has never seen the tour; the title-screen
    // Start Tutorial button pulses with the "First time?" sub-line until
    // the player either completes the 12-step tour or explicitly skips it.
    seenFullTutorial: false,
  };
}

/** The stored best for a `(mode, difficulty)` cell, or a zeroed entry. */
export function bestFor(profile: Profile, mode: RunMode, difficulty: Difficulty): BestEntry {
  return profile.bests[mode][difficulty] ?? { score: 0, wave: 0 };
}

/**
 * Flux earned by a run — a **pure function of the settled outcome** (§19.1):
 * `floor((wavesCleared × fluxPerWave + floor(finalScore / fluxScoreDivisor)) ×
 * difficultyFluxMult × modeFluxMult) + (won ? fluxWinBonus : 0)`.
 */
export function fluxForRun(outcome: RunOutcome): number {
  const base =
    outcome.wavesCleared * CONFIG.fluxPerWave +
    Math.floor(outcome.finalScore / CONFIG.fluxScoreDivisor);
  const scaled = Math.floor(
    base * CONFIG.difficultyFluxMult[outcome.difficulty] * CONFIG.modeFluxMult[outcome.mode],
  );
  return Math.max(0, scaled) + (outcome.won ? CONFIG.fluxWinBonus : 0);
}

/**
 * Settle a finished run into `profile` — award Flux, append the run-history
 * record, update the personal bests and lifetime stats (§19.5 / §20.1). The
 * caller persists the profile afterwards. Returns the Flux this run earned.
 */
export function settleRun(profile: Profile, outcome: RunOutcome): number {
  const fluxEarned = fluxForRun(outcome);

  profile.stats.runsPlayed += 1;
  profile.flux += fluxEarned;
  profile.stats.totalFluxEarned += fluxEarned;
  if (outcome.won && outcome.mode === 'campaign') profile.stats.campaignWins += 1;
  if (outcome.finalScore > profile.stats.bestScoreOverall) {
    profile.stats.bestScoreOverall = outcome.finalScore;
  }
  // Track the *true* deepest Endless wave — independent of score, so a deep
  // low-scoring run still counts (m2 — `bests.endless.wave` is score-gated).
  if (outcome.mode === 'endless' && outcome.deepestWave > profile.stats.deepestEndlessWave) {
    profile.stats.deepestEndlessWave = outcome.deepestWave;
  }

  // The bests `wave` is `wavesCleared` for Campaign/Maze, deepest for Endless.
  const bestWave = outcome.mode === 'endless' ? outcome.deepestWave : outcome.wavesCleared;
  const cur = profile.bests[outcome.mode][outcome.difficulty];
  if (!cur || outcome.finalScore > cur.score) {
    profile.bests[outcome.mode][outcome.difficulty] = {
      score: outcome.finalScore,
      wave: bestWave,
    };
  }

  profile.runHistory.push({
    run: profile.stats.runsPlayed,
    mode: outcome.mode,
    difficulty: outcome.difficulty,
    wavesCleared: outcome.wavesCleared,
    score: outcome.finalScore,
    won: outcome.won,
    overloadMax: outcome.overloadMax,
    fluxEarned,
  });
  // Keep only the last `runHistoryMax` runs — oldest evicted.
  while (profile.runHistory.length > CONFIG.runHistoryMax) profile.runHistory.shift();

  return fluxEarned;
}

/**
 * Attempt to purchase the unlock node `id` (§19.2) — succeeds iff the node
 * exists, is not already owned, every `prereq` is owned, and `flux ≥ cost`. On
 * success it deducts the Flux and appends `id`. A one-way ratchet — no refund.
 */
export function purchaseUnlock(profile: Profile, id: string): boolean {
  const node = CONFIG.unlocks.find((n) => n.id === id);
  if (!node) return false;
  if (profile.unlocked.includes(id)) return false;
  if (node.prereq !== undefined && !profile.unlocked.includes(node.prereq)) return false;
  if (profile.flux < node.cost) return false;
  profile.flux -= node.cost;
  profile.unlocked.push(id);
  return true;
}

// --- persistence -------------------------------------------------------------

/**
 * Resolve the `localStorage` key for the profile blob. The default (un-named)
 * path returns the legacy {@link CONFIG.profileKey} (`overvolt.profile`) so a
 * Sprint-9-or-later player's existing save is untouched. A named profile
 * (Sprint 54 — naive multi-profile switching via `?profile=<name>`) appends a
 * dot-separated suffix: `overvolt.profile.alice`. The name itself is the
 * caller's job to sanitise — see `Game.parseProfileNameFromUrl`.
 */
export function profileKeyFor(name?: string): string {
  return name ? `${CONFIG.profileKey}.${name}` : CONFIG.profileKey;
}

/**
 * Persist `profile` to `localStorage`. Storage failures are non-fatal. Pass
 * `name` to write a named profile (`overvolt.profile.<name>`); omitting it
 * targets the legacy default key for backward compatibility.
 */
export function saveProfile(profile: Profile, name?: string): void {
  try {
    localStorage.setItem(profileKeyFor(name), JSON.stringify(profile));
  } catch {
    // Ignore: profile persistence is a best-effort convenience.
  }
}

/** Read one legacy numeric record key, or 0 on any failure / non-positive value. */
function legacyNumber(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * One-time best-effort migration (§19.5): import any pre-Sprint-9
 * `overvolt.highscore.*` / `overvolt.endless.*` / `overvolt.maze.*` keys into
 * the profile's `bests` so an existing player keeps their records. Run once,
 * when no `overvolt.profile` blob yet exists.
 */
export function migrateLegacyKeys(profile: Profile): void {
  for (const d of DIFFICULTIES) {
    const campaignScore = legacyNumber(`${CONFIG.highScoreKey}.${d}`);
    if (campaignScore > 0) profile.bests.campaign[d] = { score: campaignScore, wave: 0 };

    const endlessScore = legacyNumber(`${CONFIG.endlessHighScoreKey}.${d}`);
    const endlessWave = legacyNumber(`${CONFIG.endlessBestWaveKey}.${d}`);
    if (endlessScore > 0 || endlessWave > 0) {
      profile.bests.endless[d] = { score: endlessScore, wave: endlessWave };
    }

    const mazeScore = legacyNumber(`${CONFIG.mazeHighScoreKey}.${d}`);
    const mazeWave = legacyNumber(`${CONFIG.mazeBestWaveKey}.${d}`);
    if (mazeScore > 0 || mazeWave > 0) {
      profile.bests.maze[d] = { score: mazeScore, wave: mazeWave };
    }
  }
}

/** A non-negative finite integer from `value`, or `fallback`. */
function intOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : fallback;
}

/** Coerce one parsed bests cell into a {@link BestEntry}, or skip it. */
function coerceBestEntry(raw: unknown): BestEntry | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  return { score: intOr(r.score, 0), wave: intOr(r.wave, 0) };
}

/** Coerce one parsed run-history row into a {@link RunRecord}, or skip it. */
function coerceRunRecord(raw: unknown): RunRecord | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const mode = r.mode;
  const difficulty = r.difficulty;
  if (mode !== 'campaign' && mode !== 'endless' && mode !== 'maze') return null;
  if (difficulty !== 'easy' && difficulty !== 'normal' && difficulty !== 'hard') return null;
  return {
    run: intOr(r.run, 0),
    mode,
    difficulty,
    wavesCleared: intOr(r.wavesCleared, 0),
    score: intOr(r.score, 0),
    won: r.won === true,
    overloadMax: intOr(r.overloadMax, 0),
    fluxEarned: intOr(r.fluxEarned, 0),
  };
}

/**
 * Coerce an arbitrary parsed value into a valid {@link Profile}, or `null` only
 * when it is wholly unrecoverable (not an object at all). Tolerant of missing
 * fields — they fill in from the fresh default.
 *
 * An older or unknown schema `version` is **migrated, not rejected** (n1): the
 * field-by-field coercion below validates every field on its own, so we recover
 * as much as the blob holds and re-stamp the current {@link PROFILE_VERSION}.
 * A bare version-mismatch reject would silently wipe a player's entire profile
 * on any future schema bump — a forward migration path is strictly safer.
 */
function coerceProfile(raw: unknown): Profile | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;

  const profile = createDefaultProfile();
  profile.flux = Math.max(0, intOr(r.flux, 0));
  if (Array.isArray(r.unlocked)) {
    profile.unlocked = r.unlocked.filter((id): id is string => typeof id === 'string');
  }
  if (typeof r.bests === 'object' && r.bests !== null) {
    const bests = r.bests as Record<string, unknown>;
    for (const mode of RUN_MODES) {
      const cell = bests[mode];
      if (typeof cell !== 'object' || cell === null) continue;
      const byDiff = cell as Record<string, unknown>;
      for (const d of DIFFICULTIES) {
        const entry = coerceBestEntry(byDiff[d]);
        if (entry) profile.bests[mode][d] = entry;
      }
    }
  }
  if (Array.isArray(r.runHistory)) {
    profile.runHistory = r.runHistory
      .map(coerceRunRecord)
      .filter((rec): rec is RunRecord => rec !== null)
      .slice(-CONFIG.runHistoryMax);
  }
  if (typeof r.stats === 'object' && r.stats !== null) {
    const s = r.stats as Record<string, unknown>;
    // A pre-`deepestEndlessWave` profile (m2) has no such field — fall back to
    // its deepest *recorded* Endless best wave, the closest value available
    // until fresh runs start tracking the true deepest going forward.
    const endlessBestWave = Math.max(
      0,
      ...DIFFICULTIES.map((d) => profile.bests.endless[d]?.wave ?? 0),
    );
    profile.stats = {
      runsPlayed: intOr(s.runsPlayed, 0),
      campaignWins: intOr(s.campaignWins, 0),
      totalFluxEarned: intOr(s.totalFluxEarned, 0),
      bestScoreOverall: intOr(s.bestScoreOverall, 0),
      deepestEndlessWave: Math.max(intOr(s.deepestEndlessWave, 0), endlessBestWave),
    };
  }
  // Schema v2 (Sprint 25) — per-tower-type first-placement hint flags. Missing
  // on any v1 profile, which is correct: a returning player who never saw the
  // role-tour gets it on their next placements, exactly as intended.
  if (typeof r.seenRoleHint === 'object' && r.seenRoleHint !== null) {
    const raw = r.seenRoleHint as Record<string, unknown>;
    const out: SeenRoleHintMap = {};
    for (const t of TOWER_TYPES) {
      if (raw[t] === true) out[t] = true;
    }
    profile.seenRoleHint = out;
  }
  // Schema v3 (Sprint 53) — per-context-id one-shot hint flags. Missing on any
  // v1 / v2 profile, which is correct: a returning player who never saw the
  // Overload / Profile-key / Endless / Puzzle hints gets them on their next
  // matching trigger, exactly as intended (mirrors the v2 → seenRoleHint path).
  if (typeof r.seenContextHint === 'object' && r.seenContextHint !== null) {
    const raw = r.seenContextHint as Record<string, unknown>;
    const out: SeenContextHintMap = {};
    for (const id of CONTEXT_HINT_IDS) {
      if (raw[id] === true) out[id] = true;
    }
    profile.seenContextHint = out;
  }
  // Schema v4 (Sprint 62 — auth-frontend-impl-s62) — the start-screen choice
  // (`guestMode`) and the cookies-banner choice (`cookiesChoice`). Missing on
  // any v1 / v2 / v3 profile, which is correct: a returning player who never
  // saw the start-screen modal or the cookies banner gets them once on their
  // next visit (the `null` defaults from `createDefaultProfile` carry through).
  // We accept only the documented values — anything else stays `null` so the
  // banner / modal re-fires (defensive — guards against a hand-edited blob).
  if (r.guestMode === true || r.guestMode === false) {
    profile.guestMode = r.guestMode;
  }
  if (r.cookiesChoice === 'accepted' || r.cookiesChoice === 'essentials') {
    profile.cookiesChoice = r.cookiesChoice;
  }
  // Schema v5 (Sprint 73 — tutorial-impl-s73) — the one-shot "has seen the
  // full 12-step opt-in tour" flag. Missing on any v1 / v2 / v3 / v4 profile,
  // which is correct: a returning player who never opted into the tour gets
  // the title-screen Start Tutorial button in its "First time?" pulsing
  // state, exactly as intended. Only the literal `true` re-stamps the flag —
  // a hand-edited blob with `'yes'` etc. defaults back to `false`, mirroring
  // the v2 / v3 defensive coercion above.
  if (r.seenFullTutorial === true) {
    profile.seenFullTutorial = true;
  }
  return profile;
}

/**
 * Load the profile from `localStorage`. A parse failure or unknown `version`
 * falls back to the fresh default — it never throws (§19.5). The first load on
 * a Sprint-9 build (no `overvolt.profile` blob yet) runs the one-time legacy
 * migration and persists the result.
 *
 * Pass `name` to load a named profile (`overvolt.profile.<name>`, Sprint 54);
 * omitting it loads the legacy default key. A fresh named profile starts
 * empty (the legacy `overvolt.highscore.*` keys belong to the default profile
 * only — a brand-new test profile should not inherit them).
 */
export function loadProfile(name?: string): Profile {
  const key = profileKeyFor(name);
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    raw = null;
  }
  if (raw === null) {
    const fresh = createDefaultProfile();
    // Legacy migration is meaningful only for the default (un-named) profile —
    // the `overvolt.highscore.*` keys were never per-named-profile, and a fresh
    // `?profile=test` blob should start clean.
    if (!name) migrateLegacyKeys(fresh);
    saveProfile(fresh, name);
    return fresh;
  }
  try {
    const coerced = coerceProfile(JSON.parse(raw) as unknown);
    if (coerced) return coerced;
  } catch {
    // Fall through to the rebuilt default below.
  }
  // The stored blob is unparseable or not a profile object. Rebuild from the
  // fresh default — but still run the one-time legacy migration (m3) so a
  // pre-Sprint-9 player keeps their `overvolt.highscore.*` records even when
  // the Sprint-9 blob itself is corrupt; the original code skipped migration
  // on this path, silently dropping those legacy scores. Same default-only
  // gate as the no-blob branch above.
  const rebuilt = createDefaultProfile();
  if (!name) migrateLegacyKeys(rebuilt);
  saveProfile(rebuilt, name);
  return rebuilt;
}

/** A deep copy of a bests grid — every {@link BestEntry} leaf is cloned (m4). */
function cloneBests(bests: BestsMap): BestsMap {
  const out = emptyBests();
  for (const mode of RUN_MODES) {
    for (const d of DIFFICULTIES) {
      const entry = bests[mode][d];
      if (entry) out[mode][d] = { score: entry.score, wave: entry.wave };
    }
  }
  return out;
}

/**
 * A deep clone of `profile` — the read-only `window.__GAME__.profile` snapshot.
 * Every nested object (including each {@link BestEntry} leaf) is copied, so a
 * test mutating the snapshot can never write through to the live profile (m4).
 */
export function cloneProfile(profile: Profile): Profile {
  return {
    version: profile.version,
    flux: profile.flux,
    unlocked: [...profile.unlocked],
    bests: cloneBests(profile.bests),
    runHistory: profile.runHistory.map((r) => ({ ...r })),
    stats: { ...profile.stats },
    seenRoleHint: { ...profile.seenRoleHint },
    seenContextHint: { ...profile.seenContextHint },
    // Sprint 62 — booleans/strings are value-typed, so the spread above is
    // pointless for them; copy explicitly to keep the snapshot honest.
    guestMode: profile.guestMode,
    cookiesChoice: profile.cookiesChoice,
    // Sprint 73 — value-typed boolean; same explicit-copy convention as
    // the Sprint 62 fields above so the snapshot is faithful to the live
    // profile (a future patch field added to `Profile` is the only path
    // through which the snapshot can drift).
    seenFullTutorial: profile.seenFullTutorial,
  };
}

/** A partial overwrite for the `setProfile` test hook (§19.5). */
export interface ProfilePatch {
  flux?: number;
  unlocked?: string[];
  bests?: Partial<BestsMap>;
  runHistory?: RunRecord[];
  stats?: Partial<ProfileStats>;
  /**
   * Sprint 73 (tutorial-impl-s73) — overwrite the "has seen the full tour"
   * flag from QA, so a test can install either the first-time-pulse state
   * (`false`) or the resting "Replay tour" affordance (`true`) without
   * driving the actual 12-step state machine.
   */
  seenFullTutorial?: boolean;
}

/**
 * Deterministically overwrite profile fields — the `setProfile` test hook
 * (§19.5). Lets QA install a fully-unlocked or fully-locked profile, or seed
 * Flux / bests / history. Unprovided fields are left untouched.
 */
export function applyProfilePatch(profile: Profile, patch: ProfilePatch): void {
  if (typeof patch.flux === 'number') profile.flux = Math.max(0, Math.trunc(patch.flux));
  if (Array.isArray(patch.unlocked)) {
    profile.unlocked = patch.unlocked.filter((id) => typeof id === 'string');
  }
  if (patch.bests) {
    for (const mode of RUN_MODES) {
      const cell = patch.bests[mode];
      if (cell) profile.bests[mode] = { ...cell };
    }
  }
  if (Array.isArray(patch.runHistory)) {
    profile.runHistory = patch.runHistory.slice(-CONFIG.runHistoryMax);
  }
  if (patch.stats) profile.stats = { ...profile.stats, ...patch.stats };
  if (typeof patch.seenFullTutorial === 'boolean') {
    profile.seenFullTutorial = patch.seenFullTutorial;
  }
}

// --- the UI-only unlock gate (docs/GAME_DESIGN.md §19.5) ---------------------
//
// These predicates filter what a *player* may pick on the title screen / HUD.
// They are consumed only by the UI layer (the Game's input handlers) — the
// simulation and the test-API sim mutators stay unlock-agnostic, so QA drives
// any tower / ability / mode / difficulty / tier regardless of profile state.

/**
 * Always-buildable placeables — never in the unlock tree (§19.2): the four
 * starter towers, plus the Maze-Mode Wall (§16.9), a free structural blocker.
 */
const STARTER_TOWERS: ReadonlySet<TowerType> = new Set<TowerType>([
  'pulse',
  'arc',
  'cryo',
  'pylon',
  'wall',
]);

/** True when an unlock node granting exactly the predicate's target is owned. */
function ownsGrant(profile: Profile, match: (g: UnlockGrant) => boolean): boolean {
  return CONFIG.unlocks.some((n) => match(n.grants) && profile.unlocked.includes(n.id));
}

/** Can this profile build `type`? The four starter towers are always available. */
export function isTowerUnlocked(profile: Profile, type: TowerType): boolean {
  if (STARTER_TOWERS.has(type)) return true;
  return ownsGrant(profile, (g) => g.kind === 'tower' && g.tower === type);
}

/** Can this profile use ability `id`? Overcharge is the starter ability. */
export function isAbilityUnlocked(profile: Profile, id: AbilityId): boolean {
  if (id === 'overcharge') return true;
  return ownsGrant(profile, (g) => g.kind === 'ability' && g.ability === id);
}

/** Can this profile select run mode `mode`? Campaign is always available. */
export function isModeUnlocked(profile: Profile, mode: RunMode): boolean {
  if (mode === 'campaign') return true;
  return ownsGrant(profile, (g) => g.kind === 'mode' && g.mode === mode);
}

/** Can this profile select `difficulty`? Easy and Normal are always available. */
export function isDifficultyUnlocked(profile: Profile, difficulty: Difficulty): boolean {
  if (difficulty !== 'hard') return true;
  return ownsGrant(profile, (g) => g.kind === 'difficulty' && g.difficulty === difficulty);
}

/** Highest Overload tier this profile may select — 2 (starter), 3 or 4. */
export function maxOverloadTier(profile: Profile): number {
  let max = 2;
  for (const node of CONFIG.unlocks) {
    if (node.grants.kind === 'overload' && profile.unlocked.includes(node.id)) {
      max = Math.max(max, node.grants.tier);
    }
  }
  return max;
}

/** True when a cosmetic board theme is owned (render-only — §11). */
export function isThemeUnlocked(profile: Profile, theme: string): boolean {
  return ownsGrant(profile, (g) => g.kind === 'theme' && g.theme === theme);
}
